/**
 * Plan entitlements — the single source of truth for what membership includes.
 *
 * THERE IS NO FREE PAGE (changed 2026-08-29). Anyone can sign up, import a CV
 * and build their page in the dashboard; publishing it to /p/[slug] is what
 * membership buys. The jobs index, search and email alerts stay free for
 * candidates permanently — only the portfolio is paid.
 *
 * Tier lives on profiles.tier ('free' | 'paid'), flipped by the Stripe webhook.
 * 'free' now means "signed up, page not live yet".
 */

/** Video applications included with membership. */
export const VIDEO_APPLICATION_QUOTA = 50;

/** Maximum length of a video application, in seconds. */
export const VIDEO_APPLICATION_MAX_SECONDS = 120;

/** Saved search tabs on the board. Free is capped; Premium gets the full lane. */
export const FREE_MAX_SEARCH_TABS = 3;
export const PAID_MAX_SEARCH_TABS = 24;

/** Email job alerts. Free gets a taste; Premium gets many. */
export const FREE_MAX_ALERTS = 2;
export const PAID_MAX_ALERTS = 20;

/*
 * Pricing (changed 2026-09-01: was a flat "$35/yr").
 *
 * We SHOW a monthly figure because that is how people judge whether a
 * subscription is cheap, but we BILL once a year. Those are two different
 * numbers, so the rule is: any surface showing the monthly figure must show
 * the annual charge next to it. PREMIUM_PRICE_AMOUNT is the ONLY number Stripe
 * ever sees — checkout builds price_data inline from it, so changing it here
 * changes what is actually charged, with no Stripe dashboard step.
 *
 * `pnpm check:pricing` enforces the invariant that the displayed monthly can
 * never imply a cheaper year than we really charge.
 */

/** What Stripe charges for the annual Premium subscription, in cents. */
export const PREMIUM_PRICE_AMOUNT = 4200;
export const PREMIUM_PRICE_CURRENCY = "usd";
/** Billing interval passed to Stripe (price_data.recurring.interval). */
export const PREMIUM_INTERVAL = "year" as const;

/** The annual charge, spelled out. Derived, so it cannot drift from the charge. */
export const PREMIUM_PRICE_ANNUAL_LABEL = `$${PREMIUM_PRICE_AMOUNT / 100}`;
/** Headline Premium price, shown on every upgrade CTA. 4200 / 12 = exactly 350. */
export const PREMIUM_PRICE_LABEL = "$3.50/mo";
/** Longer form for prose ("… for $3.50 a month"). */
export const PREMIUM_PRICE_LABEL_LONG = "$3.50 a month";
/** Always pair a monthly figure with this, so the real charge is never a surprise. */
export const PREMIUM_BILLING_NOTE = `billed annually at ${PREMIUM_PRICE_ANNUAL_LABEL}`;
/** Where upgrade CTAs send the user to actually go Premium. */
export const UPGRADE_URL = "/dashboard?tab=settings";

export interface TierBearer {
  tier?: string | null;
}

/** Whether this profile/account is on the paid plan. */
export function isPaid(bearer: TierBearer | null | undefined): boolean {
  return bearer?.tier === "paid";
}

/** Max saved search tabs for a plan. */
export function maxSearchTabs(paid: boolean): number {
  return paid ? PAID_MAX_SEARCH_TABS : FREE_MAX_SEARCH_TABS;
}

/** Max active email alerts for a plan. */
export function maxAlerts(paid: boolean): number {
  return paid ? PAID_MAX_ALERTS : FREE_MAX_ALERTS;
}

/** What membership includes — the canonical list behind every upgrade CTA. */
export const PREMIUM_FEATURES = [
  "Your live portfolio page, on your own link",
  "A 90-second video introduction on your page",
  "Video presentations of your previous work",
  `${VIDEO_APPLICATION_QUOTA} video applications, each carrying your live portfolio`,
  "Talking points written for the specific job you're applying to",
] as const;

/** One-line summary of the offer, for meta descriptions and email pitches. */
export const MEMBERSHIP_SUMMARY = `A live portfolio page with video introductions and video presentations of your work, plus ${VIDEO_APPLICATION_QUOTA} video applications with talking points written for each job you apply to.`;

/** The promise, in one clause. Used as a closer under CTAs. */
export const MEMBERSHIP_PROMISE = "Increase your chances of that first interview.";
